import { useState, useCallback, useRef } from 'react';

/**
 * useForm — lightweight controlled-form hook.
 *
 * @param {Object} initialValues  Initial field values (plain object).
 * @param {Object} [options]
 * @param {Function} [options.validate]  (values) => errorObject | null
 *   Return an object whose keys are field names and values are error strings.
 *   Return null / empty object when valid.
 * @param {Function} [options.onSubmit]  async (values) => void
 *   Called with clean values when the form passes validation.
 *   Throwing here sets a top-level `submitError`.
 *
 * @returns {Object} form helpers — see field descriptions below.
 *
 * Usage:
 *   const { values, errors, touched, handleChange, handleBlur, handleSubmit,
 *           setFieldValue, reset, submitting, submitError, isDirty } = useForm({
 *     initialValues: { name: '', email: '' },
 *     validate: (v) => {
 *       const e = {};
 *       if (!v.name) e.name = 'Name is required';
 *       if (!v.email) e.email = 'Email is required';
 *       return Object.keys(e).length ? e : null;
 *     },
 *     onSubmit: async (values) => {
 *       await api.save(values);
 *     },
 *   });
 */
export function useForm({ initialValues = {}, validate, onSubmit } = {}) {
  const initRef = useRef(initialValues);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitCount, setSubmitCount] = useState(0);

  // --- helpers ---

  /** Run validation and return errors object (or {}) */
  const runValidation = useCallback(
    (vals) => {
      if (!validate) return {};
      const result = validate(vals);
      return result && typeof result === 'object' ? result : {};
    },
    [validate],
  );

  /** Whether the current values differ from the initial snapshot */
  const isDirty = Object.keys(values).some(
    (k) => values[k] !== initRef.current[k],
  );

  // --- event handlers ---

  /**
   * Generic onChange handler for <input>, <select>, <textarea>.
   * Handles checkbox (checked) and all other inputs (value).
   */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    setValues((prev) => {
      const next = { ...prev, [name]: newVal };
      // Re-validate on change only for already-touched fields
      setErrors((prevErrors) => {
        if (!prevErrors[name]) return prevErrors;
        const newErrors = runValidation(next);
        return { ...prevErrors, [name]: newErrors[name] };
      });
      return next;
    });
  }, [runValidation]);

  /** Mark field as touched on blur, run field-level validation */
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => {
      const newErrors = runValidation(values);
      return { ...prev, [name]: newErrors[name] };
    });
  }, [runValidation, values]);

  /**
   * Programmatically set a single field value (e.g. from a date-picker or
   * custom component that doesn't fire native events).
   */
  const setFieldValue = useCallback((name, value) => {
    setValues((prev) => {
      const next = { ...prev, [name]: value };
      setErrors((prevErrors) => {
        if (!prevErrors[name]) return prevErrors;
        const newErrors = runValidation(next);
        return { ...prevErrors, [name]: newErrors[name] };
      });
      return next;
    });
  }, [runValidation]);

  /** Mark a field as touched programmatically */
  const setFieldTouched = useCallback((name, isTouched = true) => {
    setTouched((prev) => ({ ...prev, [name]: isTouched }));
  }, []);

  /** Bulk-set values (e.g. when loading an existing record for editing) */
  const setValues_ = useCallback((newValues) => {
    initRef.current = newValues;
    setValues(newValues);
    setErrors({});
    setTouched({});
    setSubmitError(null);
  }, []);

  /**
   * Reset form to its initial values (or a new snapshot if provided).
   * Passing `newInitial` also updates the baseline for `isDirty`.
   */
  const reset = useCallback((newInitial) => {
    const base = newInitial !== undefined ? newInitial : initRef.current;
    initRef.current = base;
    setValues(base);
    setErrors({});
    setTouched({});
    setSubmitError(null);
    setSubmitCount(0);
  }, []);

  /**
   * Form submit handler — attach to <form onSubmit={handleSubmit}>.
   * Validates all fields, marks all as touched, and calls `onSubmit` if valid.
   */
  const handleSubmit = useCallback(async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();

    setSubmitCount((c) => c + 1);

    // Touch every field so errors become visible
    const allTouched = Object.keys(values).reduce((acc, k) => {
      acc[k] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    const newErrors = runValidation(values);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return; // validation failed
    if (!onSubmit) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setSubmitError(err?.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  }, [values, runValidation, onSubmit]);

  return {
    values,
    errors,
    touched,
    submitting,
    submitError,
    submitCount,
    isDirty,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    /** Alias for setValues that also resets errors/touched */
    setValues: setValues_,
    reset,
  };
}

export default useForm;
